import { useEffect, useRef, useState, type FormEvent } from "react";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import type { MfaChallenge } from "@backstory/protocol";
import { deploymentMode, domainClaimedLoginMessage } from "@backstory/deployment";
import { api, ApiRequestError } from "../../api";
import { authStore } from "../../auth/store";
import { Turnstile, turnstileSiteKey } from "../../auth/Turnstile";
import { PasskeyCancelledError, getPasskey, passkeyAutofillAvailable, passkeysSupported } from "../../auth/webauthn";
import { Field } from "../../components/ui";
import { Logo, Wordmark } from "../../components/Logo";
import { GithubIcon, GoogleIcon, KeyIcon, MicrosoftIcon } from "../../components/ProviderIcons";

const PITCH = ["Replay exactly what your users saw", "Root causes explained with cited evidence", "Masked on the device, never in playback"];

const SITE_HOME = "https://www.backstory.io";

export function AuthFrame({ title, lead, children }: { title: string; lead?: string; children?: React.ReactNode }) {
  return (
    <div className="auth">
      <div className="auth-rings" aria-hidden="true" />
      <Logo size={420} className="auth-mark" />
      {deploymentMode === "saas" && (
        <a className="auth-back" href={SITE_HOME}>
          Back to site
        </a>
      )}
      <div className="auth-shell">
        <aside className="auth-aside">
          <div className="brand auth-brand">
            <Wordmark size={26} />
          </div>
          <p className="auth-pitch">The story behind every bug.</p>
          <ul className="auth-points">
            {PITCH.map((p) => (
              <li key={p}>{p}</li>
            ))}
          </ul>
        </aside>
        <div className="auth-card card">
          <div className="brand auth-card-brand">
            <Wordmark size={20} />
          </div>
          <h1>{title}</h1>
          {lead && <p className="lead">{lead}</p>}
          {children}
        </div>
      </div>
    </div>
  );
}

export function LoginPage() {
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as { next?: string; msg?: string; error?: string };
  const next = search.next && search.next.startsWith("/") ? search.next : "/";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [mode, setMode] = useState<"password" | "magic">("password");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(
    search.error === "domain_claimed" ? domainClaimedLoginMessage : null,
  );
  const [sent, setSent] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState("");
  const [turnstileTry, setTurnstileTry] = useState(0);
  const [social, setSocial] = useState<Array<"google" | "microsoft" | "github">>([]);
  useEffect(() => {
    if (deploymentMode !== "saas") return;
    let cancel = false;
    api.auth
      .providers()
      .then((p) => {
        if (!cancel) setSocial(p.social ?? []);
      })
      .catch(() => {});
    return () => {
      cancel = true;
    };
  }, []);
  const [ssoHint, setSsoHint] = useState<{ connectionId?: string; requireSso: boolean } | null>(null);

  // An instance that has never been set up has no accounts to log into.
  useEffect(() => {
    void authStore.probeSetup().then((required) => {
      if (required) void navigate({ to: "/setup", replace: true });
    });
  }, [navigate]);

  const finish = (r: Awaited<ReturnType<typeof api.auth.mfaVerify>>) => {
    authStore.applyAuthResult(r);
    // setup_mfa needs no route of its own: RequireAuth renders the blocking gate.
    if (r.next === "verify_email") void navigate({ to: "/verify-email" });
    else void navigate({ to: next as "/" });
  };
  const finishRef = useRef(finish);
  finishRef.current = finish;

  // Offer passkeys from the email field itself (the `webauthn` token on its
  // autocomplete). This is the request a credential manager extension can
  // answer, so saved passkeys show up beside saved passwords instead of only
  // behind the button below. It stays pending until a passkey is picked.
  const autofill = useRef<AbortController | null>(null);
  useEffect(() => {
    if (!passkeysSupported()) return;
    const ac = new AbortController();
    autofill.current = ac;
    void (async () => {
      if (!(await passkeyAutofillAvailable()) || ac.signal.aborted) return;
      try {
        const options = await api.auth.passkeyLoginOptions();
        const picked = await getPasskey(options, { signal: ac.signal });
        finishRef.current(await api.auth.passkeyLoginVerify(picked));
      } catch {
        // Aborted, dismissed, or nothing chosen: the form is still right there.
      }
    })();
    return () => ac.abort();
  }, []);

  const checkSso = async (value: string) => {
    if (!value.includes("@")) return;
    try {
      const r = await api.auth.ssoLookup(value);
      setSsoHint(r.connectionId ? r : null);
      if (r.requireSso && r.connectionId) window.location.assign(api.auth.ssoStartUrl(r.connectionId, next));
    } catch {
      setSsoHint(null);
    }
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      if (mode === "magic") {
        await api.auth.magicLink(email, turnstileToken);
        setSent(true);
        return;
      }
      const r = await api.auth.login({ email, password, turnstileToken });
      if ("mfaRequired" in r && r.mfaRequired) {
        const c = r as MfaChallenge;
        void navigate({ to: "/mfa", search: { challengeId: c.challengeId, methods: c.methods.join(","), next } });
        return;
      }
      finish(r as Awaited<ReturnType<typeof api.auth.mfaVerify>>);
    } catch (err) {
      const ae = err as ApiRequestError;
      setError(ae.status === 401 ? "Incorrect email or password." : ae.message);
      setTurnstileToken("");
      setTurnstileTry((n) => n + 1);
    } finally {
      setBusy(false);
    }
  };

  const passkey = async () => {
    setError(null);
    setBusy(true);
    // A pending autofill request blocks a modal one, so retire it first.
    autofill.current?.abort();
    try {
      // No challenge id here: this is a discoverable ("usernameless") login, so
      // the authenticator picks the account and the server resolves it from the
      // user handle.
      const options = await api.auth.passkeyLoginOptions();
      const r = await api.auth.passkeyLoginVerify(await getPasskey(options));
      finish(r);
    } catch (err) {
      // Dismissing the sheet is how people back out of passkey sign-in; leave
      // the form as it was rather than accusing them of an error.
      if (!(err instanceof PasskeyCancelledError)) setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <AuthFrame title="Check your email" lead={`We sent a sign-in link to ${email}. It expires in 10 minutes.`}>
        <button className="btn ghost" onClick={() => setSent(false)}>
          ← Back
        </button>
      </AuthFrame>
    );
  }

  return (
    <AuthFrame title="Sign in" lead={search.msg}>
      <form onSubmit={submit} noValidate autoComplete="off">
        <Field label="Email" error={error && !password ? undefined : undefined}>
          {(p) => (
            <input
              {...p}
              className="input"
              type="email"
              autoComplete="username webauthn"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onBlur={(e) => void checkSso(e.target.value)}
              required
            />
          )}
        </Field>
        {ssoHint?.connectionId && !ssoHint.requireSso && (
          <p className="hint" style={{ marginTop: -8 }}>
            Your organization supports single sign-on.{" "}
            <a href={api.auth.ssoStartUrl(ssoHint.connectionId, next)} style={{ color: "var(--accent)" }}>
              Continue with SSO
            </a>
          </p>
        )}
        {mode === "password" && (
          <Field label="Password" error={error === "Incorrect email or password." ? error : undefined}>
            {(p) => <input {...p} className="input" type="password" autoComplete="off" value={password} onChange={(e) => setPassword(e.target.value)} required />}
          </Field>
        )}
        <Turnstile key={turnstileTry} onToken={setTurnstileToken} />
        {error && error !== "Incorrect email or password." && (
          <div className="error-box" role="alert">
            {error}
          </div>
        )}
        <button className="btn primary block" type="submit" disabled={busy || !email || (mode === "password" && !password) || (!!turnstileSiteKey() && !turnstileToken)}>
          {busy ? "Signing in…" : mode === "password" ? "Sign in" : "Email me a sign-in link"}
        </button>
        <div className="auth-links">
          <button type="button" className="btn ghost sm" onClick={() => setMode(mode === "password" ? "magic" : "password")}>
            {mode === "password" ? "Use a magic link instead" : "Use a password instead"}
          </button>
          <Link to="/forgot">Forgot password?</Link>
        </div>
      </form>
      {deploymentMode === "saas" && social.length > 0 && (
        <>
          <div className="divider">or continue with</div>
          <div className="social-row" style={{ gridTemplateColumns: `repeat(${social.length}, 1fr)` }}>
            {social.includes("google") && (
              <a className="btn social" href={api.auth.oauthStartUrl("google", next)}>
                <GoogleIcon /> Google
              </a>
            )}
            {social.includes("microsoft") && (
              <a className="btn social" href={api.auth.oauthStartUrl("microsoft", next)}>
                <MicrosoftIcon /> Microsoft
              </a>
            )}
            {social.includes("github") && (
              <a className="btn social" href={api.auth.oauthStartUrl("github", next)}>
                <GithubIcon /> GitHub
              </a>
            )}
          </div>
        </>
      )}
      {passkeysSupported() && (
        <button className="btn block passkey-btn" type="button" onClick={() => void passkey()} disabled={busy}>
          <KeyIcon /> Sign in with a passkey
        </button>
      )}
      <div className="auth-links">
        <span className="muted">New here?</span>
        <Link to="/signup">Create an account</Link>
      </div>
    </AuthFrame>
  );
}
